import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import './Navbar.css';
import { NavLink } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ReactComponent as IconLinkedIn } from '../../assets/icons/linkedin.svg';
import LogoBlack from '../../assets/G-black.png';
import { useTheme } from '../../context/ThemeContext';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const shouldReduceMotion = useReducedMotion();
  const { theme, toggleTheme } = useTheme();

  // Optimized scroll handler using useCallback with improved behavior
  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    const scrollThreshold = 5; // Minimum scroll difference to trigger a direction change
    
    // Determine if navbar should be visible based on scroll direction with threshold
    if (currentScrollY <= 50) {
      setIsVisible(true);
      setIsScrolled(false);
    } else {
      setIsScrolled(true);
      
      // Only change visibility state if scroll difference exceeds threshold
      // or if we're at the top of the page (always show) or very far down (always hide)
      if (
        Math.abs(currentScrollY - lastScrollY) >= scrollThreshold ||
        currentScrollY <= 100 ||
        currentScrollY > 1000
      ) {
        // Improved logic: show when scrolling up or near top, hide when scrolling down significantly
        const scrollingUp = currentScrollY < lastScrollY;
        const nearTop = currentScrollY <= 100;
        setIsVisible(scrollingUp || nearTop);
      }
    }
    
    setLastScrollY(currentScrollY);
  }, [lastScrollY]);

  useEffect(() => {
    // Use passive listener for better performance
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const toggleMenu = useCallback(() => {
    setIsMenuOpen(prev => !prev);
  }, []);

  // Memoize navigation links to prevent unnecessary re-renders
  const navLinks = useMemo(() => [
    { to: '/', label: 'Home' },
    { to: '/about', label: 'About' },
    { to: '/portfolio', label: 'Portfolio' },
    { to: '/news', label: 'News' },
    { to: '/contact', label: 'Contact' },
    { to: '/cv', label: 'CV' }
  ], []);

  // Close menu when pressing Escape key
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [isMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  // Animation variants with reduced motion support and improved transitions
  const navVariants = {
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { 
        type: shouldReduceMotion ? "tween" : "spring", 
        stiffness: 100, 
        damping: 20,
        duration: shouldReduceMotion ? 0.2 : 0.4
      }
    },
    hidden: { 
      y: -100, 
      opacity: 0,
      transition: { 
        type: shouldReduceMotion ? "tween" : "spring", 
        stiffness: 80, 
        damping: 15,
        duration: shouldReduceMotion ? 0.2 : 0.3
      }
    }
  };

  // Enhanced mobile menu staggered animation variants
  const menuItemVariants = {
    open: (i) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        delay: shouldReduceMotion ? 0 : i * 0.08, // Increased stagger effect
        duration: 0.4,
        type: shouldReduceMotion ? "tween" : "spring",
        stiffness: 120,
        damping: 8
      }
    }),
    closed: {
      opacity: 0,
      y: -15,
      scale: 0.92,
      transition: {
        duration: 0.2,
        type: shouldReduceMotion ? "tween" : "spring",
        stiffness: 100
      }
    }
  };
  
  // Backdrop animation variants
  const backdropVariants = {
    open: {
      opacity: 1,
      backdropFilter: "blur(4px)",
      transition: {
        duration: shouldReduceMotion ? 0.2 : 0.3
      }
    },
    closed: {
      opacity: 0,
      backdropFilter: "blur(0px)",
      transition: {
        duration: 0.2
      }
    }
  };

  return (
    <motion.header 
      variants={navVariants}
      animate={isVisible ? "visible" : "hidden"}
      initial="visible"
      className={`fixed w-full z-50 transition-all duration-500 ${
        isScrolled 
          ? 'py-0.5 bg-white dark:bg-secondary-900 bg-opacity-95 backdrop-blur-lg shadow-md dark:shadow-black/40' 
          : 'py-1.5 bg-transparent'
      }`}
      role="banner"
    >
      <div className="container mx-auto px-4 sm:px-6">
        <nav className="flex justify-between items-center py-2" role="navigation" aria-label="Main Navigation">
          {/* Logo */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ 
              duration: shouldReduceMotion ? 0.1 : 0.5, 
              type: shouldReduceMotion ? "tween" : "spring", 
              stiffness: 100 
            }}
            className="text-2xl font-bold text-primary-600"
          >
            <NavLink 
              to="/" 
              className="flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 rounded-md" 
              aria-label="Gerald Suleh - Home"
            >
              <img 
                src={LogoBlack} 
                alt="Gerald Suleh" 
                className="h-9 md:h-11 w-auto flex-shrink-0 transition-all duration-300 hover:scale-105 hover:brightness-110 hover:contrast-125" 
              />
            </NavLink>
          </motion.div>

          {/* Desktop Navigation */}
          <motion.ul 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ 
              duration: shouldReduceMotion ? 0.1 : 0.5, 
              delay: shouldReduceMotion ? 0 : 0.2, 
              type: shouldReduceMotion ? "tween" : "spring", 
              stiffness: 80 
            }}
            className="hidden md:flex space-x-8"
            role="menubar"
          >
            {navLinks.map((link, index) => (
              <li key={index} role="none" className="relative">
                <NavLink 
                  to={link.to}
                  className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                  role="menuitem"
                >
                  {link.label}
                </NavLink>
              </li>
            ))}
          </motion.ul>

          {/* Social Links */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ 
              duration: shouldReduceMotion ? 0.1 : 0.5, 
              type: shouldReduceMotion ? "tween" : "spring", 
              stiffness: 100 
            }}
            className="hidden md:flex items-center space-x-4"
          >
            <button
              onClick={toggleTheme}
              className="text-secondary-600 dark:text-secondary-200 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-300 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-full p-1"
              aria-label="Toggle dark mode"
            >
              {theme === 'dark' ? (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>
            <a 
              href="https://www.linkedin.com/in/gerald-suleh/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-secondary-600 dark:text-secondary-200 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-300 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 rounded-full p-1"
              aria-label="LinkedIn Profile"
            >
              <IconLinkedIn className="h-5 w-5" />
            </a>
            <a
              href="/Gerald_Suleh_-_Senior_Engineer-SOC.pdf"
              download="Gerald_Suleh_-_Senior_Engineer-SOC.pdf"
              className="bg-primary-600 hover:bg-primary-700 text-white px-5 py-2.5 rounded-md transition-all duration-300 shadow-sm hover:shadow-md hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
              aria-label="Download CV"
            >
              Download CV
            </a>
          </motion.div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button 
              onClick={toggleMenu}
              className="text-secondary-800 dark:text-secondary-200 p-2 rounded-md transition-all duration-300 hover:bg-secondary-100 dark:hover:bg-secondary-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-6 w-6" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </nav>

        {/* Mobile Menu Overlay */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              variants={backdropVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className="fixed inset-0 bg-black bg-opacity-20 z-40 md:hidden"
              onClick={() => setIsMenuOpen(false)}
              aria-hidden="true"
            />
          )}
        </AnimatePresence>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div 
              id="mobile-menu"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ 
                duration: shouldReduceMotion ? 0.1 : 0.3, 
                type: shouldReduceMotion ? "tween" : "spring", 
                stiffness: 150 
              }}
              className="md:hidden mt-2 pb-6 pt-4 px-4 bg-white dark:bg-secondary-900 shadow-xl dark:shadow-black/40 backdrop-blur-lg rounded-b-xl z-50 relative overflow-hidden"
              role="menu"
            >
              <ul className="flex flex-col space-y-5 pb-2">
                {navLinks.map((link, index) => (
                  <motion.li 
                    key={index} 
                    role="none"
                    custom={index}
                    variants={menuItemVariants}
                    initial="closed"
                    animate="open"
                    exit="closed"
                  >
                    <NavLink 
                      to={link.to}
                      onClick={() => setIsMenuOpen(false)}
                      className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`}
                      role="menuitem"
                    >
                      {link.label}
                    </NavLink>
                  </motion.li>
                ))}
                <motion.li 
                  className="pt-2" 
                  role="none"
                  custom={navLinks.length}
                  variants={menuItemVariants}
                  initial="closed"
                  animate="open"
                  exit="closed"
                >
                  <a
                    href="/Gerald_Suleh_-_Senior_Engineer-SOC.pdf"
                    download="Gerald_Suleh_-_Senior_Engineer-SOC.pdf"
                    onClick={() => setIsMenuOpen(false)}
                    className="inline-block bg-primary-600 hover:bg-primary-700 text-white px-5 py-3 rounded-md transition-all duration-300 shadow-sm hover:shadow-md hover:scale-[1.02] w-full text-center font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                    role="menuitem"
                  >
                    Download CV
                  </a>
                </motion.li>
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

// Optimize with memo to prevent unnecessary re-renders
export default memo(Navbar);

